/*!
 * Copyright (c) Microsoft Corporation.
 * All rights reserved. See LICENSE in the project root for license information.
 */

import {
    IComponent,
    IComponentProps,
    ICoreContext,
    IGridSettings,
    IImageData,
    IImageSettings,
    Image,
    IRequestContext,
    msdyn365Commerce
} from '@msdyn365-commerce/core';
import { getFallbackImageUrl, getProductPageUrlSync } from '@msdyn365-commerce-modules/retail-actions';
import { getPayloadObject, getTelemetryAttributes, ITelemetryContent, onTelemetryClick } from '@msdyn365-commerce-modules/utilities';
import * as React from 'react';

import { IFeatureProduct } from '../models/feature-product';
import { ISearchResultContainerV2Resources } from '../search-result-container-v2.props.autogenerated';

/**
 * IFeatureSearchResultData interface.
 */
export interface IFeatureSearchResultData {
    product: IFeatureProduct | null;
    context: ICoreContext;
    imageSettings?: IImageSettings;
    telemetryContent?: ITelemetryContent;
    recommendation?: string;
}

/**
 * Feature Search Result Item view props.
 */
interface IFeatureSearchResultItemProps extends IComponentProps<IFeatureSearchResultData> {}

/**
 * IFeatureSearchResultComponent Interface.
 */
export interface IFeatureSearchResultComponent extends IComponent<IFeatureSearchResultItemProps> {}

/**
 * Method to render product title.
 * @param title - Product title.
 * @returns Renders the product title.
 */
function renderProductTitle(title?: string): JSX.Element | null {
    return <h1 className='msc-feature-search-result__item__product-title'>{title}</h1>;
}

/**
 * Method to render product image.
 * @param imageSettings - Image setting for the product image.
 * @param gridSettings - Grid setting for the site.
 * @param imageUrl - Image url.
 * @param altText - Alt text for the image.
 * @param fallbackImage - Fallback image url in case main image is not present.
 * @param context - Current request context.
 * @returns Renders the product image.
 */
function renderProductPlacementImage(
    imageSettings?: IImageSettings,
    gridSettings?: IGridSettings,
    imageUrl?: string,
    altText?: string,
    fallbackImage?: string,
    context?: IRequestContext
): JSX.Element | null {
    if (!imageUrl || !gridSettings || !imageSettings) {
        return null;
    }
    const image: IImageData = {
        src: imageUrl,
        altText: altText ? altText : ''
    };
    const imageProps = {
        gridSettings,
        imageSettings,
        fallBackSrc: fallbackImage
    };
    return <Image {...image} {...imageProps} loadFailureBehavior='empty' requestContext={context} />;
}

// eslint-disable-next-line jsdoc/require-param -- The actual eslint fix is asking for repeated list of same parameter 5 times.
/**
 * Method to render Feature product.
 * @returns Renders the feature product component.
 */
const FeatureSearchResultFunctionalComponent: React.FC<IFeatureSearchResultItemProps> = (props: IFeatureSearchResultItemProps) => {
    const {
        data: { product, imageSettings, telemetryContent, recommendation },
        context
    } = props;
    const resources = props.moduleResources as ISearchResultContainerV2Resources;
    if (product === null) {
        return null;
    }

    const productName = product.name;
    const productRecordId = product.recordId ? product.recordId : 0;
    const productUrl = getProductPageUrlSync(product.name ?? '', productRecordId, context.actionContext, undefined);
    const fallbackImage = getFallbackImageUrl(
        product.itemId,
        context.actionContext.requestContext.apiSettings,
        context.request.app?.config?.OmniChannelMedia
    );
    const heading =
        recommendation && recommendation === 'descriptions' ? resources.featureSimilarDescriptionTitle : resources.featureSimilarLooksTitle;

    // Construct telemetry attribute to render
    const payLoad = getPayloadObject('click', telemetryContent!, productName!, productRecordId.toString());

    const attribute = getTelemetryAttributes(telemetryContent!, payLoad);
    return (
        <div className='ms-feature-search-result__item'>
            <a
                href={productUrl}
                onClick={onTelemetryClick(telemetryContent!, payLoad, product.name!)}
                {...attribute}
                aria-label={product.name}
            >
                <div className='msc-feature-search-result__item__image'>
                    {renderProductPlacementImage(
                        imageSettings,
                        context.request.gridSettings,
                        product.primaryImageUrl,
                        product.name,
                        fallbackImage,
                        context.actionContext.requestContext
                    )}
                </div>
            </a>
            <div className='msc-feature-search-result__item__detail'>
                <p className='msc-feature-search-result__item__heading'>{heading}</p>
                {renderProductTitle(product.name)}
            </div>
        </div>
    );
};

/**
 * Feature state result component.
 */
export const FeatureSearchResultComponent: React.FunctionComponent<IFeatureSearchResultItemProps> = msdyn365Commerce.createComponent<
    IFeatureSearchResultComponent
>('Feature-Search-Result-Item', { component: FeatureSearchResultFunctionalComponent });
